import { ColumnPicker } from '@components/ui/ColumnPicker';
import type { DataSheetColumn } from '@components/ui/DataSheet';
import usePersistentState from '@hooks/usePersistentState';
import type { ReactNode } from 'react';

/**
 * The stock grid's columns after two decisions: what the brand's setup makes meaningful, then
 * what this person chose to hide. Shared by the brand's site page and the site's own Stock page,
 * so the two can never disagree about which figures exist.
 *
 * QC pending and the batch count only mean something where the kind is batch-tracked. Rejected
 * always shows: untracked, it is what was kept bad at the gate.
 * The picker offers only what is on offer: a switch for a column that cannot appear does nothing.
 */
export function useStockColumns<T>(
    columns: DataSheetColumn<T>[],
    args: { tracked: boolean; storageKey: string },
): { columns: DataSheetColumn<T>[]; picker: ReactNode } {
    const [hidden, setHidden] = usePersistentState<string[]>(args.storageKey, []);

    const ruledOut = new Set(args.tracked ? [] : ['inQc', 'batches']);
    const offered = columns.filter((column) => !ruledOut.has(column.key));
    // The item column is the row's name; hiding it would leave numbers belonging to nothing.
    const hideable = offered.filter((column) => !column.sticky && typeof column.header === 'string');

    return {
        columns: offered.filter((column) => column.sticky || !hidden.includes(column.key)),
        picker: (
            <ColumnPicker
                defaultOnDefs={hideable.map((column) => ({ key: column.key, label: String(column.header) }))}
                defaultOffDefs={[]}
                hiddenKeys={hidden}
                onChange={setHidden}
            />
        ),
    };
}
